if (!customElements.get('product-videos')) {
  class ProductVideos extends HTMLElement {
    constructor() {
      super();
      this.handleKey = this.handleKey.bind(this);
    }

    connectedCallback() {
      this.tiles = Array.from(this.querySelectorAll('[data-product-videos-tile]'));
      this.modal = this.querySelector('[data-product-videos-modal]');
      this.video = this.querySelector('[data-product-videos-video]');
      this.media = this.modal && this.modal.querySelector('.product-videos-modal__media');
      this.currentIndex = -1;

      if (!this.modal || !this.video) return;

      this.tiles.forEach((tile) => {
        tile.addEventListener('click', (event) => {
          event.preventDefault();
          const idx = parseInt(tile.dataset.index, 10);
          if (Number.isFinite(idx)) this.open(idx);
        });
      });

      this.modal.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-action]');
        if (!btn) return;
        const action = btn.dataset.action;
        switch (action) {
          case 'close': this.close(); break;
          case 'prev': this.go(-1); break;
          case 'next': this.go(1); break;
          case 'play-pause': this.togglePlay(); break;
          case 'mute': this.toggleMute(); break;
          case 'fullscreen': this.enterFullscreen(); break;
        }
      });

      this.video.addEventListener('play', () => { this.modal.dataset.state = 'playing'; });
      this.video.addEventListener('pause', () => { this.modal.dataset.state = 'paused'; });
      this.video.addEventListener('volumechange', () => {
        this.modal.dataset.muted = String(this.video.muted);
      });

      this.wheelLock = false;
      this.modal.addEventListener('wheel', (event) => {
        if (!this.modal.classList.contains('is-open')) return;
        event.preventDefault();
        if (this.wheelLock) return;
        if (Math.abs(event.deltaY) < 10) return;
        this.wheelLock = true;
        this.go(event.deltaY > 0 ? 1 : -1);
        setTimeout(() => { this.wheelLock = false; }, 350);
      }, { passive: false });

      this.touchStartY = null;
      this.modal.addEventListener('touchstart', (event) => {
        if (!this.modal.classList.contains('is-open')) return;
        this.touchStartY = event.touches[0].clientY;
      }, { passive: true });
      this.modal.addEventListener('touchend', (event) => {
        if (this.touchStartY === null) return;
        const endY = event.changedTouches[0].clientY;
        const delta = this.touchStartY - endY;
        this.touchStartY = null;
        if (Math.abs(delta) > 50) {
          this.go(delta > 0 ? 1 : -1);
        }
      });
    }

    isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    loadFromTile(index) {
      if (index < 0 || index >= this.tiles.length) return false;
      const tile = this.tiles[index];
      const src = tile.dataset.src;
      if (!src) return false;
      const mime = tile.dataset.mime || 'video/mp4';
      const poster = tile.dataset.poster || '';
      const alt = tile.dataset.alt || '';
      this.currentIndex = index;
      this.loadVideo(src, mime, poster, alt);
      return true;
    }

    lockBodyScroll() {
      if (this.bodyLocked) return;
      this.savedScrollY = window.scrollY;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
      this.bodyLocked = true;
    }

    unlockBodyScroll() {
      if (!this.bodyLocked) return;
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.paddingRight = '';
      window.scrollTo(0, this.savedScrollY || 0);
      this.bodyLocked = false;
    }

    open(index) {
      if (!this.loadFromTile(index)) return;

      this.video.muted = true;
      this.modal.dataset.muted = 'true';
      this.modal.removeAttribute('hidden');
      if (this.isMobile()) this.lockBodyScroll();
      requestAnimationFrame(() => this.modal.classList.add('is-open'));

      const playPromise = this.video.play();
      if (playPromise && playPromise.catch) playPromise.catch(() => {});

      document.addEventListener('keydown', this.handleKey);
    }

    close() {
      if (!this.modal.classList.contains('is-open')) return;
      this.modal.classList.remove('is-open');
      this.video.pause();
      this.unlockBodyScroll();
      setTimeout(() => {
        if (!this.modal.classList.contains('is-open')) {
          this.modal.setAttribute('hidden', '');
          this.video.removeAttribute('src');
          this.video.load();
          this.resetMediaTransform();
        }
      }, 300);
      document.removeEventListener('keydown', this.handleKey);
    }

    resetMediaTransform() {
      if (!this.media) return;
      this.media.style.transition = '';
      this.media.style.transform = '';
      this.media.style.opacity = '';
    }

    go(direction) {
      if (this.tiles.length === 0 || this.transitioning || this.currentIndex < 0 || !this.media) return;
      const next = (this.currentIndex + direction + this.tiles.length) % this.tiles.length;
      if (next === this.currentIndex) return;
      this.transitioning = true;

      const exitY = direction > 0 ? -100 : 100;
      const enterY = -exitY;
      const duration = 250;

      this.media.style.transition = `transform ${duration}ms ease, opacity ${duration}ms ease`;
      this.media.style.transform = `translateY(${exitY}%)`;
      this.media.style.opacity = '0';

      setTimeout(() => {
        this.video.pause();
        this.loadFromTile(next);

        this.media.style.transition = 'none';
        this.media.style.transform = `translateY(${enterY}%)`;
        this.media.style.opacity = '0';
        void this.media.offsetWidth;

        this.media.style.transition = `transform ${duration}ms ease, opacity ${duration}ms ease`;
        this.media.style.transform = '';
        this.media.style.opacity = '';

        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});

        setTimeout(() => {
          this.resetMediaTransform();
          this.transitioning = false;
        }, duration + 30);
      }, duration + 10);
    }

    togglePlay() {
      if (this.video.paused) {
        const p = this.video.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        this.video.pause();
      }
    }

    toggleMute() {
      this.video.muted = !this.video.muted;
    }

    enterFullscreen() {
      const fn = this.video.requestFullscreen
        || this.video.webkitRequestFullscreen
        || this.video.webkitEnterFullscreen
        || this.video.msRequestFullscreen;
      if (fn) {
        try { fn.call(this.video); } catch (e) { /* no-op */ }
      }
    }

    loadVideo(src, mime, poster, alt) {
      this.video.setAttribute('src', src);
      if (poster) this.video.setAttribute('poster', poster);
      else this.video.removeAttribute('poster');
      this.video.setAttribute('aria-label', alt);
      this.video.load();
    }

    handleKey(event) {
      if (event.key === 'Escape') {
        this.close();
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.go(-1);
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.go(1);
      }
    }
  }
  customElements.define('product-videos', ProductVideos);
}

if (!customElements.get('product-videos-hover')) {
  class ProductVideosHover extends HTMLElement {
    connectedCallback() {
      this.target = this.closest('[data-product-videos-tile]') || this;
      this.video = null;
      this.target.addEventListener('pointerenter', this.activate.bind(this));
      this.target.addEventListener('pointerleave', this.deactivate.bind(this));
      this.target.addEventListener('focusin', this.activate.bind(this));
      this.target.addEventListener('focusout', this.deactivate.bind(this));
    }

    ensureVideo() {
      if (this.video) return this.video;
      const src = this.dataset.src;
      const mime = this.dataset.mime || 'video/mp4';
      if (!src) return null;
      const v = document.createElement('video');
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.preload = 'none';
      v.className = 'product-videos__tile-video';
      const source = document.createElement('source');
      source.src = src;
      source.type = mime;
      v.appendChild(source);
      this.appendChild(v);
      this.video = v;
      return v;
    }

    activate() {
      const v = this.ensureVideo();
      if (!v) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }

    deactivate() {
      if (!this.video) return;
      this.video.pause();
      try { this.video.currentTime = 0; } catch (e) { /* no-op */ }
    }
  }
  customElements.define('product-videos-hover', ProductVideosHover);
}
